import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache } from "@apollo/client";
import React from "react";
import * as ReactDOM from "react-dom";
import { ComponentOverview } from "./components/ComponentOverview";
import { EmailWizard } from "./containers/EmailWizard";
import { TemplateWizard } from "./containers/TemplateWizard";
import { AdvancedEmailWizardContextProvider } from "./context";
import { ErrorContextProvider } from "./context/ErrorContext";
import { DynamicTextContextEnum } from "./enums/DynamicTextContext";
import { WizardTypeEnum } from "./enums/WizardTypeEnum";

declare global {
    // These can be used to render different pages of the app into SRW (see visitors react app for examples)
    interface Window {
        RenderAdvancedEmail: any;
        RenderAdvancedEmailTemplate: any;

        RenderComponentOverview: any;
    }
}

/* Renders the component overview */
window.RenderComponentOverview = (divId: string) => ReactDOM.render(<ComponentOverview />, document.getElementById(divId) as HTMLElement);

/* Renders the Advanced Email Wizard*/
window.RenderAdvancedEmail = (
    divId: string,
    ids: number[],
    connection: string,
    credentials: boolean,
    wizardType: WizardTypeEnum,
    dynamicTextContext: DynamicTextContextEnum,
    events: IAdvancedEmailAppEvents,
) => {
    const onClose = () => ReactDOM.unmountComponentAtNode(document.getElementById(divId)!);
    const emailWizard = <EmailWizard
        EntryIDs={ids}
        OnWizardClose={onClose}
        Events={events}
    />;

    return RenderWizard(divId, connection, credentials, wizardType, dynamicTextContext, emailWizard);
};

/* Renders the Email Template Wizard*/
window.RenderAdvancedEmailTemplate = (
    divId: string,
    id: number,
    connection: string,
    credentials: boolean,
    events: IAdvancedTemplateAppEvents,
) => {
    const onClose = () => ReactDOM.unmountComponentAtNode(document.getElementById(divId)!);
    const templateWizard = <TemplateWizard
        TemplateID={id}
        OnWizardClose={onClose}
        Events={{
            ...events,
            deleteEmailAttachment: () => Promise.resolve({
                success: true,
            }),
        }}
    />;

    return RenderWizard(divId, connection, credentials, WizardTypeEnum.Template, DynamicTextContextEnum.Template, templateWizard);
};

const RenderWizard = (divId: string, connection: string, credentials: boolean, wizardType: WizardTypeEnum, dynamicTextContext: DynamicTextContextEnum, wizard: JSX.Element): void => {
    const advancedEmailClient = new ApolloClient({
        link: new HttpLink({
            uri: connection,
            credentials: credentials ? "include" : "",
        }),
        cache: new InMemoryCache({
            typePolicies: {
                Communication: {
                    keyFields: [],
                },
            },
        }),
    });

    const containerElement = document.getElementById(divId);
    if (!containerElement) {
        return;
    }

    return ReactDOM.render(
        <ApolloProvider client={advancedEmailClient}>
            <ErrorContextProvider>
                <AdvancedEmailWizardContextProvider
                    IsLoading={true}
                    WizardType={wizardType}
                    DynamicTextContext={dynamicTextContext}
                >
                    {wizard}
                </AdvancedEmailWizardContextProvider>
            </ErrorContextProvider>
        </ApolloProvider>
        , document.getElementById(divId) as HTMLElement);
};
